import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import Button from "@source/components/UI/buttons/button/button";
import Checkbox from "@source/components/UI/buttons/checkbox/checkbox";
import Radio from "@source/components/UI/buttons/radio/radio";
import FileInput from "@source/components/UI/inputs/fileInput/fileInput";
import SelectInput from "@source/components/UI/inputs/selectInput/selectInput";
import TextInput from "@source/components/UI/inputs/textInput/textInput";
import AutocompleteInput, {ISelectItem} from "@source/components/UI/inputs/autocompleteInput/autocompleteInput";
import Chip from "@source/components/UI/chip/chip";
import Notification from "@source/components/snackMessage/notification";
import appStore from "@source/store/appStore";
import nursingHomeStore from "@source/store/nursingHomeStore";
import userStore from "@source/store/userStore";
import ResidentController from "@source/api/controllers/residentController";
import EmployeeController from "@source/api/controllers/employeeController";
import ConsentController from "@source/api/controllers/consentController";
import {
  ConsentCategory, ConsentCategorySource, ConsentCategoryStatus, ConsentDocument, ConsentDocumentStatus,
  ConsentSignerRole, ConsentTemplate, ConsentTemplateInput, ConsentTemplateInputType,
} from "@source/api/models/consentModels";
import {
  ConsentStepsValues, createConsentStepsPayload, emptyConsentStepsValues,
  hasValidConfirmedConsentPdf, normalizeConsentIds,
  isConsentPdfHeader, sortConsentInputsByDocument, validateConsentCategorySelection, validateConsentPdfFile, validateConsentPdfFiles, validateConsentSteps,
  isResidentImmediatelyActiveAfterSend, residentPresentationPath,
} from "@source/api/models/consentHelpers";
import useIncrementalConsentList from "@source/hooks/useIncrementalConsentList";
import iconPictureFileInput from "@source/assets/icons/iconPictureFileInput.svg";
import iconCross from "@source/assets/icons/iconCross.svg";
import Svg from "@source/components/UI/svg/svg";
import {ConsentAutoLoad, ConsentRequestState} from "@source/components/consentDocuments";
import {ConsentDocumentPreview} from "@source/components/consentDocuments/consentDocumentPreview";
import styles from "./consentSteps.module.scss";

interface Person { _id: string; firstName: string; lastName: string; }
interface SignerPerson extends Person { secondaryLabel?: string; }
const nameOf = (person: Person) => `${person.firstName} ${person.lastName}`.trim();
const signerLabel = (person: SignerPerson) =>
  [nameOf(person), person.secondaryLabel].filter(Boolean).join(" · ");
const contactTypeLabel = (value?: string) => value ? `${value.charAt(0).toUpperCase()}${value.slice(1)} contact` : "";
const uniqueSignerPeople = (people: SignerPerson[]) =>
  people.filter((person, index) => people.findIndex((candidate) => candidate._id === person._id) === index);
const actionableError = (error: unknown, fallback: string) => {
  const status = typeof error === "object" && error !== null && "status" in error ? (error as {status?: number}).status : undefined;
  if (status === 400) return "Some selected people or fields are no longer valid. Review the form and try again.";
  if (status === 403) return "You do not have permission for this nursing home or consent document.";
  if (status === 404) return "This consent resource is no longer available. Return to consent history and try again.";
  if (status === 409) return "This draft changed or is no longer editable. Refresh the document to see its current state.";
  return error instanceof Error ? error.message : fallback;
};
type PersistStage = "saving" | "uploading" | "confirming" | "sending" | null;
const stageText: Record<Exclude<PersistStage, null>, string> = {
  saving: "Saving draft", uploading: "Uploading PDF", confirming: "Confirming PDF", sending: "Sending",
};
const readableFileSize = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(bytes < 1024 * 1024 ? 2 : 1)} MiB`;
const pdfHeaderIsValid = async (file: File) => {
  const header = await file.slice(0, 5).arrayBuffer();
  return isConsentPdfHeader(new Uint8Array(header));
};
const rolesText = (category: Pick<ConsentCategory, "requiredSignerRoles" | "signingOrderRequired" | "signingOrder">) => {
  const names: Record<ConsentSignerRole, string> = {
    [ConsentSignerRole.EMPLOYEE]: "Employees", [ConsentSignerRole.FAMILY_MEMBER]: "Family members", [ConsentSignerRole.RESIDENT]: "Resident",
  };
  const order = category.signingOrder.filter((role) => category.requiredSignerRoles.includes(role));
  return category.signingOrderRequired ? `Role order: ${(order.length ? order : category.requiredSignerRoles).map((role) => names[role]).join(" → ")}` : "Roles may sign in any order";
};
const escapePreviewHtml = (value: string) => value
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
const previewDate = (value: unknown) => {
  if (!value) return "";
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};
const previewText = (value: unknown) => typeof value === "string" || typeof value === "number" ? String(value) : "";
const previewName = (person?: {firstName?: string; lastName?: string}) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(" ");
const previewInputValue = (input: ConsentTemplateInput, value: unknown) => {
  const labels = new Map((input.options || []).map((option) => [option.id, option.label]));
  if (input.type === ConsentTemplateInputType.CHECKBOX) return value === true ? "Yes" : "";
  if (Array.isArray(value)) return value.map((item) => labels.get(String(item)) ?? String(item)).join(", ");
  if (typeof value === "string") return labels.get(value) ?? value;
  return "";
};
const templatePreviewDocument = (template: ConsentTemplate, resident: any, values: ConsentStepsValues) => {
  const employee = userStore.getUserInfo();
  const home = nursingHomeStore.activeNursingHome;
  const familyId = values.familyFieldCompleterId || (values.familyMemberIds.length === 1 ? values.familyMemberIds[0] : "");
  const relation = (resident?.familyRelations || []).find((item: any) => item?.familyMember?._id === familyId);
  const family = relation?.familyMember;
  const doctor = resident?.attendedDoctor;
  const filled = new Map<string, string>([
    ["document.title", template.title],
    ["resident.firstName", previewText(resident?.firstName)],
    ["resident.lastName", previewText(resident?.lastName)],
    ["resident.name", previewName(resident)],
    ["resident.gender", previewText(resident?.gender)],
    ["resident.room", previewText(resident?.room)],
    ["resident.section", previewText(resident?.section)],
    ["resident.birthdate", previewDate(resident?.birthdate)],
    ["resident.admissionDate", previewDate(resident?.admissionDate)],
    ["resident.createdDate", previewDate(resident?.createdDate)],
    ["resident.attendedDoctor.firstName", previewText(doctor?.firstName)],
    ["resident.attendedDoctor.lastName", previewText(doctor?.lastName)],
    ["resident.attendedDoctor.name", previewName(doctor)],
    ["employee.firstName", previewText(employee?.firstName)],
    ["employee.lastName", previewText(employee?.lastName)],
    ["employee.name", previewName(employee || undefined)],
    ["employee.email", previewText(employee?.email)],
    ["employee.phoneNumber", previewText(employee?.phoneNumber)],
    ["employee.role", previewText(employee?.account?.role)],
    ["employee.staffId", previewText(employee?.account?.stuffId)],
    ["employee.position", previewText(employee?.account?.position)],
    ["employee.employeeType", previewText(employee?.account?.employeeType)],
    ["nursingHome.name", previewText(home?.name)],
    ["nursingHome.address", previewText(home?.address)],
    ["nursingHome.phone", previewText(home?.phone)],
    ["nursingHome.email", previewText(home?.email)],
    ["family.firstName", previewText(family?.firstName)],
    ["family.lastName", previewText(family?.lastName)],
    ["family.name", previewName(family)],
    ["family.email", previewText(family?.email)],
    ["family.phoneNumber", previewText(family?.phoneNumber)],
    ["family.relationship", previewText(relation?.relationship)],
    ["family.contactType", previewText(relation?.contactType)],
  ]);
  template.employeeInputs.forEach((input) => {
    filled.set(`employee.${input.id}`, previewInputValue(input, values.employeeInputValues[input.id]));
  });
  const html = template.structure.html.replace(
    /{{\s*([a-zA-Z][a-zA-Z0-9_]*(?:\.[a-zA-Z][a-zA-Z0-9_-]*){1,2})\s*}}/g,
    (_placeholder, key: string) => escapePreviewHtml(filled.get(key) ?? ""),
  );
  return `<!doctype html><html><head><meta charset="utf-8"><style>${template.structure.css || ""}</style></head><body>${html}</body></html>`;
};
const valuesFromDraft = (draft: ConsentDocument): ConsentStepsValues => ({
  categoryId: draft.categoryId || draft.categorySnapshot?.categoryId || "",
  employeeSignerIds: normalizeConsentIds(draft.requiredEmployeeSigners.map((person) => person._id)),
  familyMemberIds: normalizeConsentIds(
    (draft.familyMembers?.length ? draft.familyMembers : draft.familyMember ? [draft.familyMember] : [])
      .map((person) => person._id),
  ),
  familyFieldCompleterId: draft.familyFieldCompleter?._id || "",
  employeeInputValues: draft.employeeInputValues || {},
});

export const ConsentSteps = ({nursingHomeId, organizationId}: {nursingHomeId: string; organizationId: string}) => {
  const history = useHistory();
  const {residentId, documentId} = useParams<{residentId: string; documentId?: string}>();
  const [step, setStep] = useState(1);
  const [values, setValues] = useState<ConsentStepsValues>(emptyConsentStepsValues);
  const [draft, setDraft] = useState<ConsentDocument | null>(null);
  const [resident, setResident] = useState<any>(null);
  const [employees, setEmployees] = useState<Person[]>([]);
  const [template, setTemplate] = useState<ConsentTemplate | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [templateError, setTemplateError] = useState("");
  const [formError, setFormError] = useState("");
  const [inputErrors, setInputErrors] = useState<Record<string, string>>({});
  const [stale, setStale] = useState(false);
  const [persistStage, setPersistStage] = useState<PersistStage>(null);
  const [selectedPdf, setSelectedPdf] = useState<File | null>(null);
  const [sentDocument, setSentDocument] = useState<ConsentDocument | null>(null);
  const mountedRef = useRef(true);
  const detailsRequestRef = useRef(0);
  const templateRequestRef = useRef(0);
  const persistRequestRef = useRef(0);
  const persistingRef = useRef(false);
  const categoryRequest = useMemo(() => (query: {page: number; limit: number}) =>
    ConsentController.listCategories(organizationId, {...query, status: ConsentCategoryStatus.ACTIVE, sort: "asc"}), [organizationId]);
  const categories = useIncrementalConsentList<ConsentCategory, {}>(categoryRequest, {});
  const selected = categories.items.find((category) => category._id === values.categoryId) || null;
  const selectedSnapshot = draft?.categorySnapshot?.categoryId === values.categoryId ? draft.categorySnapshot : null;
  const selectedCategory = selected || selectedSnapshot;
  const selectedCategorySource = selectedCategory?.source;
  const selectedTemplateId = selectedCategory?.templateId;
  const categoryUnavailable = Boolean(selectedSnapshot && !selected && !categories.isLoading && !categories.hasMore);
  const cancel = useCallback(
    () => history.push(`/home/residents/${residentId}/consent-documents`),
    [history, residentId],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      persistRequestRef.current += 1;
    };
  }, []);
  useEffect(() => {
    const residentName = [resident?.firstName, resident?.lastName].filter(Boolean).join(" ") || "Resident";
    const header = {
      resident: resident || undefined,
      title: `${documentId ? "Edit" : "New"} consent document for ${residentName}`,
      subtitle: `Step ${step} of 2 · ${step === 1 ? "Select Consent Category" : "Complete consent details"}`,
      onBack: sentDocument ? cancel : step === 2 ? () => setStep(1) : cancel,
      usePreviousStepNavigation: true,
      onCancel: cancel,
    };
    appStore.setConsentResidentHeader(header);
    return () => appStore.resetConsentResidentHeader(header);
  }, [documentId, resident, sentDocument, step, cancel]);

  const loadDetails = useCallback(async (reset = false) => {
    const requestId = ++detailsRequestRef.current;
    if (!mountedRef.current) return;
    if (reset) {
      templateRequestRef.current += 1;
      setStep(1);
      setValues(emptyConsentStepsValues());
      setDraft(null);
      setResident(null);
      setEmployees([]);
      setTemplate(null);
      setTemplateError("");
      setFormError("");
      setInputErrors({});
      setStale(false);
      setPersistStage(null);
      setSelectedPdf(null);
      setSentDocument(null);
    }
    setLoadingDetails(true); setDetailError("");
    try {
      const [loadedResident, loadedEmployees, loadedDraft] = await Promise.all([
        ResidentController.getResident({nursingHomeId, residentId}),
        EmployeeController.getNames(nursingHomeId),
        documentId ? ConsentController.getDocument(nursingHomeId, documentId) : Promise.resolve(null),
      ]);
      if (!mountedRef.current || requestId !== detailsRequestRef.current) return;
      if (loadedDraft && loadedDraft.resident._id !== residentId) {
        new Notification().error("This draft belongs to a different resident and cannot be edited from this URL.");
        history.replace(`/home/residents/${residentId}/consent-documents`);
        return;
      }
      if (loadedDraft && loadedDraft.status !== ConsentDocumentStatus.DRAFT) {
        new Notification().error("This consent document is no longer a draft and cannot be edited.");
        history.replace(`/home/residents/${residentId}/consent-documents`);
        return;
      }
      setResident(loadedResident); setEmployees(loadedEmployees as Person[]); setDraft(loadedDraft);
      setValues(loadedDraft ? valuesFromDraft(loadedDraft) : emptyConsentStepsValues());
      setFormError("");
      setStale(false);
    } catch (error: unknown) {
      if (mountedRef.current && requestId === detailsRequestRef.current) setDetailError(actionableError(error, "Unable to load consent details."));
    } finally {
      if (mountedRef.current && requestId === detailsRequestRef.current) setLoadingDetails(false);
    }
  }, [documentId, history, nursingHomeId, residentId]);
  useEffect(() => { void loadDetails(true); }, [loadDetails]);

  const loadTemplate = useCallback(async () => {
    const requestId = ++templateRequestRef.current;
    if (selectedCategorySource !== ConsentCategorySource.TEMPLATE || !selectedTemplateId) {
      if (mountedRef.current) {
        setTemplate(null);
        setTemplateError("");
      }
      return;
    }
    setTemplate(null); setTemplateError("");
    try {
      const loaded = await ConsentController.getTemplate(organizationId, selectedTemplateId);
      if (mountedRef.current && requestId === templateRequestRef.current) setTemplate(loaded);
    } catch (error: unknown) {
      if (mountedRef.current && requestId === templateRequestRef.current) setTemplateError(actionableError(error, "Unable to load the selected template."));
    }
  }, [organizationId, selectedCategorySource, selectedTemplateId]);
  useEffect(() => { if (step === 2) void loadTemplate(); }, [loadTemplate, step]);

  const chooseCategory = (id: string) => {
    if (id !== values.categoryId) {
      setSelectedPdf(null);
    }
    setValues((current) => {
    if (id === current.categoryId) return current;
    const next = categories.items.find((category) => category._id === id);
    const compatible = (role: ConsentSignerRole) => Boolean(next?.requiredSignerRoles.includes(role));
    return {...current, categoryId: id, employeeInputValues: {}, familyFieldCompleterId: "", employeeSignerIds: compatible(ConsentSignerRole.EMPLOYEE) ? current.employeeSignerIds : [], familyMemberIds: compatible(ConsentSignerRole.FAMILY_MEMBER) ? current.familyMemberIds : []};
    });
  };
  const addSigner = (key: "employeeSignerIds" | "familyMemberIds", id: string) => setValues((current) => ({
    ...current, [key]: normalizeConsentIds([...current[key], id]),
  }));
  const removeSigner = (key: "employeeSignerIds" | "familyMemberIds", id: string) => setValues((current) => {
    const remaining = current[key].filter((item) => item !== id);
    return {
      ...current,
      [key]: remaining,
      ...(key === "familyMemberIds"
        && (current.familyFieldCompleterId === id || remaining.length <= 1)
        ? {familyFieldCompleterId: ""}
        : {}),
    };
  });
  const previewDocument = useMemo(
    () => (template ? templatePreviewDocument(template, resident, values) : ""),
    [resident, template, values],
  );
  const orderedEmployeeInputs = useMemo(
    () => sortConsentInputsByDocument(template?.employeeInputs ?? [], template?.structure?.html, "employee"),
    [template],
  );
  const hasConfirmedPdf = hasValidConfirmedConsentPdf(draft, values.categoryId, residentId);
  const hasCurrentDraftSource = Boolean(
    draft && draft.resident._id === residentId
    && (draft.categoryId || draft.categorySnapshot?.categoryId) === values.categoryId
    && (selectedCategorySource === ConsentCategorySource.TEMPLATE || hasConfirmedPdf),
  );
  const selectPdf = (file: File | null) => {
    if (!file) { setSelectedPdf(null); return; }
    const error = validateConsentPdfFile(file);
    if (error) { setSelectedPdf(null); return; }
    setSelectedPdf(file); setFormError("");
  };
  const validatePdfFiles = (files: File[]) => validateConsentPdfFiles(files);
  const validateStepOne = () => validateConsentCategorySelection(selected, categoryUnavailable);
  const validatePersistence = (forSend: boolean) => {
    if (!selected) return categoryUnavailable
      ? "This Consent Category is no longer active. Select another active category."
      : "Wait for active Consent Categories to finish loading.";
    const stepsError = validateConsentSteps(selectedCategory, template, values, forSend);
    if (stepsError) return stepsError;
    if (forSend && selectedCategory?.source === ConsentCategorySource.RAW_PDF && !selectedPdf && !hasConfirmedPdf) {
      return "Attach a PDF before sending this consent document.";
    }
    return "";
  };
  const persist = async (send: boolean) => {
    if (persistStage || persistingRef.current) return;
    const error = validatePersistence(send);
    const incomplete = send ? orderedEmployeeInputs.filter((input) => {
      const value = values.employeeInputValues[input.id];
      if (input.type === ConsentTemplateInputType.TEXT) return !(typeof value === "string" && value.trim());
      if (input.type === ConsentTemplateInputType.RADIO) return typeof value !== "string" || !value;
      return false;
    }) : [];
    if (error && error !== "Complete every staff text field before sending.") {
      setFormError(error);
      setInputErrors({});
      return;
    }
    if (incomplete[0]) {
      setFormError("Complete the highlighted field before sending.");
      setInputErrors(Object.fromEntries(incomplete.map((input) => [input.id, input.type === ConsentTemplateInputType.RADIO ? "Select an option before sending." : "Complete this field before sending."])));
      window.requestAnimationFrame(() => {
        document.getElementById(`consent-field-${incomplete[0].id}`)?.scrollIntoView({behavior: "smooth", block: "center"});
      });
      return;
    }
    if (error) { setFormError(error); return; }
    setInputErrors({});
    if (!selectedCategory || (selectedCategory.source === ConsentCategorySource.TEMPLATE && (!template || templateError))) {
      setFormError("Wait for the selected template to load, then try again."); return;
    }
    persistingRef.current = true;
    const requestId = ++persistRequestRef.current;
    let stage: Exclude<PersistStage, null> = "saving";
    const setStage = (next: Exclude<PersistStage, null>) => { stage = next; setPersistStage(next); };
    setStage("saving"); setFormError(""); setStale(false);
    try {
      const payload = createConsentStepsPayload(residentId, selectedCategory, template, values);
      let result = draft
        ? await ConsentController.updateDocument(nursingHomeId, draft._id, payload)
        : await ConsentController.createDocument(nursingHomeId, payload);
      setDraft(result);
      if (selectedPdf) {
        if (!(await pdfHeaderIsValid(selectedPdf))) throw new Error("The selected file does not have a valid PDF header.");
        setStage("uploading");
        const target = await ConsentController.createPdfUploadUrl(nursingHomeId, result._id);
        await ConsentController.uploadPdf(target.uploadUrl, selectedPdf);
        setStage("confirming");
        result = await ConsentController.confirmPdf(nursingHomeId, result._id, {
          objectKey: target.objectKey, originalFileName: selectedPdf.name, mimeType: "application/pdf",
        });
        setDraft(result);
        setSelectedPdf(null);
      }
      if (send) {
        setStage("sending");
        result = await ConsentController.sendDocument(nursingHomeId, result._id);
      }
      if (!mountedRef.current || requestId !== persistRequestRef.current) return;
      try {
        new Notification().success(send ? "Consent document sent." : "Consent document saved as draft.");
      } catch {
        // Notification failures must not recast a completed mutation as an error.
      }
      if (send && isResidentImmediatelyActiveAfterSend(result)) {
        setSentDocument(result);
      } else {
        history.push(`/home/residents/${residentId}/consent-documents`);
      }
    } catch (error: unknown) {
      const message = actionableError(error, "Unable to save the consent document.");
      const status = typeof error === "object" && error !== null && "status" in error ? (error as {status?: number}).status : undefined;
      setFormError(status === 409 ? message : `${stageText[stage]} failed: ${message}`);
      setStale(status === 409);
    } finally {
      persistingRef.current = false;
      if (mountedRef.current && requestId === persistRequestRef.current) setPersistStage(null);
    }
  };

  if (loadingDetails) return <ConsentRequestState variant="loading" title="Loading Consent Steps"/>;
  if (detailError) return <ConsentRequestState variant="error" title="Unable to load Consent Steps" description={detailError} onRetry={() => void loadDetails()}/>;
  const family = uniqueSignerPeople((resident?.familyRelations || [])
    .filter((relation: any) => relation.status === "active" && relation.familyMember?._id && !relation.familyMember.isDisabled)
    .map((relation: any) => ({...relation.familyMember, secondaryLabel: [contactTypeLabel(relation.contactType), relation.relationship].filter(Boolean).join(" · ")} as SignerPerson)));
  const roleRequired = (role: ConsentSignerRole) => Boolean(selectedCategory?.requiredSignerRoles.includes(role));
  if (sentDocument) return <div className={styles.steps}>
    <section className={styles.sentNext}><h2>Consent document sent</h2><p>The resident is ready to review this consent document in person.</p>
      <div className={styles.actions}><Button variant="outlined" onClick={cancel}>Present later</Button><Button event="link" link={residentPresentationPath(residentId, sentDocument._id)}>Present to resident now</Button></div>
    </section>
  </div>;
  return <div className={styles.steps}>
    {step === 1 ? <section className={styles.categoryStep}>
      {categoryUnavailable && selectedSnapshot ? <div className={styles.unavailable} role="alert"><strong>{selectedSnapshot.name}</strong> is no longer active. Select another Consent Category before continuing.</div> : null}
      {categories.isLoading && !categories.items.length ? <ConsentRequestState variant="loading" title="Loading active Consent Categories"/> : null}
      {categories.error && !categories.items.length ? <ConsentRequestState variant="error" title="Unable to load Consent Categories" description={categories.error} onRetry={categories.retry}/> : null}
      {categories.items.length ? <><SelectInput id="consent-category" className={styles.formControl} label="Consent Category" required value={values.categoryId} placeholder="Select a consent category" items={categories.items.map((category) => ({id: category._id, value: category._id, title: category.name}))} disabled={Boolean(persistStage)} handleChange={(event) => chooseCategory(event.target.value)}/>
        {selected ? <p className={styles.categorySupport}>{selected.source === ConsentCategorySource.TEMPLATE ? "Template source" : "PDF source"} · Required roles: {selected.requiredSignerRoles.map((role) => role.replace(/_/g, " ")).join(", ")} · {rolesText(selected)}</p> : null}</> : null}
      {!categories.isLoading && !categories.error && !categories.items.length ? <ConsentRequestState variant="empty" title="No active Consent Categories" description="Ask an administrator to configure an active Consent Category."/> : null}
      {categories.error && categories.items.length ? <div className={styles.categoryRetry} role="alert"><span>More Consent Categories could not be loaded. {categories.error}</span><Button size="small" variant="outlined" onClick={categories.retry}>Retry</Button></div> : null}
      <ConsentAutoLoad hasMore={categories.hasMore} isLoading={categories.isLoading} error={categories.error} onLoadMore={categories.loadMore}/>
      {formError ? <p className={styles.error} role="alert">{formError}</p> : null}
      <div className={styles.actions}><Button onClick={() => { const error = validateStepOne(); if (error) setFormError(error); else { setFormError(""); setStep(2); }}} disabled={!selected || categoryUnavailable || Boolean(persistStage)}>Continue</Button></div>
    </section> : <section className={styles.detailsStep}>
      <div className={styles.stepBody}>
      <div className={styles.summary}><strong>{selectedCategory?.name}</strong><span>{selectedCategory?.source === ConsentCategorySource.TEMPLATE ? "Template document" : "Raw PDF document"} · {selectedCategory ? rolesText(selectedCategory as ConsentCategory) : ""}</span></div>
      {selectedCategory?.source === ConsentCategorySource.RAW_PDF ? 
      <section className={styles.rawState}><strong>Source PDF</strong>{hasConfirmedPdf 
      ? <span><strong>{draft?.pdf?.originalFileName}</strong> is attached to this draft. You can replace the attachment before sending.</span> 
      : <span>Attach one PDF only · maximum 25 MiB.</span>}{selectedPdf 
      ? <div className={styles.selectedPdf}><span>{selectedPdf.name} · {readableFileSize(selectedPdf.size)}</span>
      <button type="button" className={styles.clearReplacement} onClick={() => selectPdf(null)} disabled={Boolean(persistStage)} aria-label="Clear replacement" title="Clear replacement"><Svg src={iconCross} color="#7E808F" width={14} height={14}/></button></div> : 
      <div className={`${styles.updateFileUnputMultiple}`}>
        <div className={styles.photo}>
        <FileInput 
        areaTextClassName={styles.areaText}
        subAreaTextClassName={styles.subAreaText}
        className={styles.img}
        btnClassName={styles.imgBtn}
      selectedFile={null} 
      setSelectedFile={selectPdf} 
      loading={persistStage === "uploading" || persistStage === "confirming"} 
      disabled={Boolean(persistStage)} acceptFormats="application/pdf,.pdf" 
      validateFiles={validatePdfFiles}
      uploadImg={iconPictureFileInput} imgColor="#7E808F" imgWidth={40} imgHeight={40} areaText="Drop a PDF here, or" subAreaText="PDF only · maximum 25 MiB"
      /></div></div>}</section> : null}
      {selectedCategory?.source === ConsentCategorySource.RAW_PDF && hasCurrentDraftSource && draft ? <section className={styles.previewSource}><h3>Preview current source</h3><p>This shows the saved source currently attached to this draft. Local file selections are not previewed until saved and confirmed.</p><ConsentDocumentPreview documentId={draft._id} autoLoad={false} loadLabel="Preview current source"/></section> : null}
      {template ? <section className={styles.previewSource}><h3>Document preview</h3><p>Updates as you complete the fields below.</p><iframe className={styles.previewFrame} sandbox="" srcDoc={previewDocument} title="Consent document preview"/></section> : null}
      {selectedCategory?.source === ConsentCategorySource.TEMPLATE && !template && !templateError ? <ConsentRequestState variant="loading" title="Loading template fields"/> : null}
      {templateError ? <ConsentRequestState variant="error" title="Unable to load template fields" description={templateError} onRetry={loadTemplate}/> : null}
      {orderedEmployeeInputs.length ? <section className={styles.section}><h3>Complete fields below before sending consent</h3>{orderedEmployeeInputs.map((input) => <TemplateInput key={input.id} input={input} value={values.employeeInputValues[input.id]} error={inputErrors[input.id]} disabled={Boolean(persistStage)} onChange={(value) => {
        setInputErrors((current) => {
          if (!current[input.id]) return current;
          const next = {...current};
          delete next[input.id];
          return next;
        });
        setValues((current) => ({...current, employeeInputValues: {...current.employeeInputValues, [input.id]: value}}));
      }}/>)}</section> : null}
      {roleRequired(ConsentSignerRole.EMPLOYEE) ? <PeoplePicker id="employee-signers" label="Required Employee signatures" placeholder="Select an employee to add" people={employees} selected={values.employeeSignerIds} disabled={Boolean(persistStage)} onAdd={(id) => addSigner("employeeSignerIds", id)} onRemove={(id) => removeSigner("employeeSignerIds", id)}/> : null}
      {roleRequired(ConsentSignerRole.FAMILY_MEMBER) ? <><PeoplePicker id="family-signers" label="Required Family members signatures" placeholder="Select a family member to add" people={family} selected={values.familyMemberIds} disabled={Boolean(persistStage)} onAdd={(id) => addSigner("familyMemberIds", id)} onRemove={(id) => removeSigner("familyMemberIds", id)}/>{selectedCategory?.source === ConsentCategorySource.TEMPLATE && values.familyMemberIds.length > 1 ? <SelectInput id="family-field-completer" className={styles.formControl} label="Family member completing fields" placeholder="Select a family member" value={values.familyFieldCompleterId} items={family.filter((person) => values.familyMemberIds.includes(person._id)).map((person) => ({id: person._id, value: person._id, title: signerLabel(person)}))} disabled={Boolean(persistStage)} handleChange={(event) => setValues({...values, familyFieldCompleterId: event.target.value})} required/> : null}</> : null}
      {stale ? <Button variant="outlined" onClick={() => void loadDetails()} disabled={Boolean(persistStage)}>Refresh document</Button> : null}
    </div>
      <div className={styles.actions}>
        {formError ? <p className={styles.error} role="alert">{formError}</p> : null}
        {persistStage ? <p className={styles.stage} role="status">{stageText[persistStage]}…</p> : null}
        <div className={styles.actionButtons}><Button variant="outlined" onClick={() => persist(false)} isLoading={persistStage === "saving"}>Save as draft</Button><Button onClick={() => persist(true)} isLoading={persistStage === "sending"}>Send</Button></div>
      </div>
    </section>}
  </div>;
};

const PeoplePicker = ({id, label, placeholder, people, selected, disabled, onAdd, onRemove}: {id: string; label: string; placeholder: string; people: SignerPerson[]; selected: string[]; disabled: boolean; onAdd: (id: string) => void; onRemove: (id: string) => void}) => {
  const [search, setSearch] = useState("");
  const uniquePeople = uniqueSignerPeople(people);
  const selectedIds = normalizeConsentIds(selected);
  const available = uniquePeople.filter((person) => !selectedIds.includes(person._id));
  const selectedPeople = selectedIds.map((id) => uniquePeople.find((person) => person._id === id)).filter(Boolean) as SignerPerson[];
  const items: ISelectItem[] = available.map((person) => ({id: person._id, value: person._id, title: signerLabel(person)}));

  return <section className={styles.section}>
    <AutocompleteInput
      id={id}
      label={label}
      required
      placeholder={placeholder}
      value={null}
      items={items}
      inputItems={items}
      inputValue={search}
      disabled={disabled || !available.length}
      handleChange={(_, person) => {
        if (person) onAdd(person.value);
        setSearch("");
      }}
      onInputChange={(_, value) => setSearch(value)}
    />
    {selectedPeople.length ? <div className={styles.selectedPeople}>
      {selectedPeople.map((person) => <Chip key={person._id} id={person._id} text={signerLabel(person)} onDelete={disabled ? undefined : onRemove}/>)}
    </div> : null}
    {!people.length ? <p>No eligible people are available.</p> : null}
  </section>;
};

const TemplateInput = ({input, value, disabled, error, onChange}: {input: any; value: unknown; disabled: boolean; error?: string; onChange: (value: unknown) => void}) => {
  const fieldId = `consent-field-${input.id}`;
  if (input.type === ConsentTemplateInputType.TEXT) return <div id={fieldId}><TextInput id={`consent-input-${input.id}`} className={`${styles.formControl} ${styles.templateTextInput}`} label={input.label} value={typeof value === "string" ? value : ""} multiline={input.multiline} disabled={disabled} error={error} touched={Boolean(error)} handleChange={(event) => onChange(event.target.value)}/></div>;
  if (input.type === ConsentTemplateInputType.CHECKBOX) return <div id={fieldId}><Checkbox className={styles.templateBooleanInput} name={`consent-input-${input.id}`} value={input.id} checked={value === true} disabled={disabled} onChange={(event) => onChange(event.target.checked)} label={input.label}/></div>;
  if (input.type === ConsentTemplateInputType.RADIO) return <div id={fieldId} className={styles.inputGroup}><strong>{input.label}</strong>{(input.options || []).map((option: any) => <Radio key={option.id} name={`consent-input-${input.id}`} value={option.id} checked={value === option.id} disabled={disabled} onChange={() => onChange(option.id)} label={option.label}/>)}{error ? <p className={styles.fieldError}>{error}</p> : null}</div>;
  const checked = Array.isArray(value) ? value : [];
  return <div id={fieldId} className={styles.inputGroup}><strong>{input.label}</strong>{(input.options || []).map((option: any) => <Checkbox key={option.id} name={`consent-input-${input.id}-${option.id}`} value={option.id} checked={checked.includes(option.id)} disabled={disabled} onChange={() => onChange(checked.includes(option.id) ? checked.filter((id: string) => id !== option.id) : [...checked, option.id])} label={option.label}/>)}</div>;
};
